/**
 * Canonical ledger permission strings, copied verbatim from
 * openapi/generated/route_policy.gen.go. Controls are gated on the exact
 * permission the route demands, so no rendered button can 403.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { ModuleBoundary } from "@/features/apps/module-boundary";
import { useSession } from "@/lib/session/use-session";

export const LEDGER_PERMISSIONS = {
  accountRead: "ledger.account.read",
  accountCreate: "ledger.account.create",
  accountUpdate: "ledger.account.update",
  accountDelete: "ledger.account.delete",
  accountImport: "ledger.account.import",
  accountImportPreview: "ledger.account.import.preview",
  journalRead: "ledger.journal.read",
  journalPost: "ledger.journal.post",
  journalReverse: "ledger.journal.reverse",
  trialBalanceRead: "ledger.trial-balance.read",
  fxRateRead: "ledger.fx-rate.read",
  fxRateManualCreate: "ledger.fx-rate.manual.create",
  fiscalPeriodRead: "ledger.fiscal-period.read",
  fiscalPeriodTransition: "ledger.fiscal-period.transition",
  fiscalCalendarGenerate: "ledger.fiscal-calendar.generate",
  postingRuleRead: "ledger.posting-rule.read",
  postingRuleCreate: "ledger.posting-rule.create",
  postingRuleUpdate: "ledger.posting-rule.update",
  postingRulePreview: "ledger.posting-rule.preview",
  businessEventRead: "ledger.business-event.read",
  businessEventReplay: "ledger.business-event.replay",
} as const;

export type LedgerPermission = (typeof LEDGER_PERMISSIONS)[keyof typeof LEDGER_PERMISSIONS];

/** Direct-route authorization: denied pages never mount or issue queries. */
export function LedgerPermissionBoundary({
  permission,
  children,
}: {
  permission: LedgerPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission, hasModule } = useSession();

  // The app comes before the grant, and in that order deliberately. If the
  // company has not installed this app, saying "you lack permission" sends
  // somebody to an administrator who cannot help — the fix is to install it,
  // not to be granted something. The server answers the same way round.
  if (!hasModule("accounting")) return <ModuleBoundary app="accounting">{children}</ModuleBoundary>;

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card
        role="alert"
        aria-labelledby="ledger-forbidden-title"
        className="w-full max-w-xl border-destructive/30 bg-card/95 shadow-sm"
        data-testid="ledger-route-forbidden"
      >
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <h1 id="ledger-forbidden-title" className="text-xl font-semibold tracking-tight">
              {t("ledger.permissions.title")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
